import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { Subject }    from 'rxjs/Subject';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/timeout';
import { Config } from './config';
import { Product } from '../Models/product';
import { Page } from '../Models/page';
import { Cart } from '../Models/cart';
import { ProductFilter } from '../Models/filter';
import { ProductBase } from './product.base';


@Injectable()
export class ProductService extends ProductBase {

  /**
   * Listener definition for changes on the product list that a
   * component may use. This is only to reflect changes on the whole
   * list, not changes on individual products. These can be caught by
   * the promise that the service will return the new data.
   */
  private productsChangedSrc = new Subject<Product>();

  productsChanged$ = this.productsChangedSrc.asObservable();

  constructor(private http: HttpClient, private config: Config) {
    super();
  }

  getParamsFromFilter(filter: ProductFilter): HttpParams {
    var params = new HttpParams();

    if (filter) {
      var keys = filter.keys();

      for (var i= 0; i < keys.length; i++) {

        if (filter.hasval(keys[i])) {
          if (typeof filter[keys[i]] === 'string') {
            let kw = keys[i] === 'query' ? 'search' : keys[i];
            params = params.append(kw, filter[keys[i]]);
          } else if (typeof filter[keys[i]] === 'number') {
            params = params.append(keys[i], filter[keys[i]]);
          } else{
            for (var j= 0; j < filter[keys[i]].length; j++) {
              if (filter[keys[i]]) {
                params = params.append(keys[i], filter[keys[i]][j]);
              }
            }
          }
        }
      }
    }

    return params;
  }

  list(filter?: ProductFilter): Observable<Page<Product>> {

    var params = this.getParamsFromFilter(filter);

    return this.http.get<Product[]>(
      this.config.getURL('products'),
      { params: params }
    ).map(
      data =>
        {
          return this.createPage(data);
        }
    );
  }

  /**
   * Get related products. Relations can be followed over provided keys.
   */
  public related(product: Product, keys?: string[]): Observable<Page<Product>> {

    var params = new HttpParams();

    if (keys) {

      for (var i= 0; i < keys.length; i++) {
        params = params.append('key', keys[i]);
      }
    }

    params = params.append('limit', '8');

    return this.http.get<Product[]>(
      product._links.related,
      { params: params }
    ).map(
      data =>
        {
          return this.createPage(data);
        }
    )
  }

  get(product: number): Observable<Product> {
    return this.http.get<Product>(
      `${this.config.getURL('products')}${product}/`,
      { observe: 'response' }
    ).map(

      data => {

        var prod = this.cast(data.body);
        prod.permissions = data.headers.get('Allow').split(', ');

        return prod;
      }
    );
  }

  /**
   * Create product and notify the event handler that something has
   * changed...
   */
  public create(data: Product): Observable<Product> {
    return this.http.post<Product>(this.config.getURL('products'), data).
      timeout(this.config.request_timeout).
      map(
      product => {

        this.productsChangedSrc.next(<Product>data);

        return product
      }
    )
  }

  public update(product: Product): Observable<Product> {
    return this.http.put<Product>(product._links['self'], product);
  }

  /**
   * Remove product and notify any listeners to changes on the
   * product list.
   */
  public remove(product: Product): any {

    return this.http.delete(product._links['self']).map(
      data => {
        this.productsChangedSrc.next(product);

        return data
      })
  }

  /**
   * Revive product and return changed product.
   */
  public revive(product: Product): Observable<Product> {

    return this.http.post<Product>(product._links['revive'], {}).map(
      data => {
        return this.cast(data);
      })
  }

  follow(product: Product): Observable<Product> {

    return this.http.post<Product>(product._links['follow'], {}).map(
      data => {
        return this.cast(data);
      }
    );
  }

  unFollow(product: Product): Observable<Product> {
    return this.http.post<Product>(product._links['unfollow'], {}).
      timeout(this.config.request_timeout).map(
        data => {
          return this.cast(data);
        }
      );
  }

  /**
   * Register product view.
   */
  see(product: Product): Observable<Product> {

    return this.http.post<Product>(product._links['see'], {}).map(
      data => {
        return this.cast(data);
      }
    );
  }

  /**
   * Post a question about this product to the vendor.
   */
  ask(product: Product, msg: any): Observable<any> {

    return this.http.post<any>(product._links['ask'], msg);
  }

  /**
   * Mark a product as shipped
   */
  markShipped(product: Product): Observable<Product> {

    return this.http.post<Product>(product._links['mark_shipped'], {});
  }
}



// WEBPACK FOOTER //
// ./src/app/Services/product.ts